import {splitAt} from '../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {RuleModelStrict} from '../../models/api/ruleModel';
import {decorateWithClientId} from '../editors/commons/clientIdModel';
import {DelayParameter} from '../editors/commons/paramModel';
import {ClientRuleAction, RuleActionTypesEnum} from './ruleActionModel';
import {ANDConditions, RuleCondition, RuleConditionTypesEnum} from './ruleConditionModel';
import {mapToClientConditions} from './ruleEditor/recipes/helpers/recipesHelpers';
import {findRuleAction, findRuleCondition, splitANDconditions} from './ruleHelpers';

export enum CsatRuleRolesEnum {
  SURVEY_SCHEDULED = 'surveyScheduled',
  SURVEY_SENT = 'surveySent',
  REMOVE_SCHEDULED_ON_INBOUND = 'removeScheduledTagOnInbound',
}

export enum CsatRuleErrorsEnum {
  MISSING_AUTO_REPLY = 'missingAutoReply',
}

type PartialCsatDelayParameter = Partial<DelayParameter>;

export function partialCsatDelayToDelayParameter(
  partialDelayParameter?: PartialCsatDelayParameter,
): DelayParameter | undefined {
  if (partialDelayParameter?.unit && partialDelayParameter?.val) {
    const {unit, val} = partialDelayParameter;
    return {unit, val};
  }

  return undefined;
}

type CsatDelay = {
  delay?: PartialCsatDelayParameter;
};

function equals(a: CsatDelay, b: CsatDelay): boolean {
  const insideA = partialCsatDelayToDelayParameter(a.delay);
  const insideB = partialCsatDelayToDelayParameter(b.delay);

  const insideEquals =
    (insideA === undefined && insideB === undefined) ||
    (insideA && insideB && DelayParameter.equals(insideA, insideB));

  return Boolean(insideEquals);
}

const emptyDelay: CsatDelay = {};

const CsatDelay = {
  empty: emptyDelay,
  equals,
};

interface CsatTags {
  surveyScheduled?: number;
  surveySent?: number;
}

interface CsatActions {
  surveyScheduled: ReadonlyArray<ClientRuleAction>;
  surveySent: ReadonlyArray<ClientRuleAction>;
}

interface CsatInitialRules {
  sentRule: RuleModelStrict;
  scheduledRule: RuleModelStrict;
  unscheduleRule?: RuleModelStrict;
}

interface CsatState {
  ruleName: string;
  conditions: ANDConditions;
  tags: CsatTags;
  timeDelay: CsatDelay;
  actions: CsatActions;
  csatResponse?: number;
  initialRules: CsatInitialRules;
  csatBundleError?: CsatRuleErrorsEnum;
}

// We hide the 'has_tag' 'hasnt_tag' and 'delay_since_trigger' conditions for sent.
const HIDDEN_SENT_FIRST_CONDITIONS: ReadonlyArray<RuleConditionTypesEnum> = [
  RuleConditionTypesEnum.HAS_TAG,
  RuleConditionTypesEnum.HASNT_TAG,
  RuleConditionTypesEnum.DELAY_SINCE_TRIGGER,
];
const HIDDEN_SENT_FIRST_CONDITIONS_COUNT = HIDDEN_SENT_FIRST_CONDITIONS.length;

interface CsatStateProps {
  csatRules: ReadonlyArray<RuleModelStrict>;
  recipeName?: string;
}

// List of Rules -> Csat state. Dual of 'toCreateCsatBundle'.
export function initialCsatState(props: CsatStateProps): CsatState {
  const {csatRules, recipeName} = props;

  const sentRule = maybeFindCsatRule(csatRules, CsatRuleRolesEnum.SURVEY_SENT);
  const scheduledRule = maybeFindCsatRule(csatRules, CsatRuleRolesEnum.SURVEY_SCHEDULED);
  const unscheduleRule = maybeFindCsatRule(csatRules, CsatRuleRolesEnum.REMOVE_SCHEDULED_ON_INBOUND);

  if (!sentRule || !scheduledRule) {
    throw new Error(
      `Rules for survey sent and scheduled not found among ${csatRules.map((r) => r.role || '').join(', ')}`,
    );
  }

  const sentTagAction = findRuleAction(sentRule, RuleActionTypesEnum.TAG);
  const scheduledTagAction = findRuleAction(scheduledRule, RuleActionTypesEnum.TAG);
  const csatReplyAction = findRuleAction(sentRule, RuleActionTypesEnum.REPLY);

  const tags: CsatTags = {
    surveyScheduled: scheduledTagAction?.params?.tag,
    surveySent: sentTagAction?.params?.tag,
  };

  const conditions = getConditions(sentRule);

  // Sanity check the first available condition
  const firstCondition = conditions[0]?.[0];
  if (firstCondition?.type !== RuleConditionTypesEnum.INBOX_IN) {
    console.error(
      'First CSAT condition is not an `inbox_in`. Are the conditions ordered correctly ?',
      conditions,
    );
  }

  const actions: CsatActions = {
    surveySent: sentRule.actions
      .slice(3) // Ignore rules represented in other fields (tag sent, remove tag scheduled and auto-reply)
      .map(decorateWithClientId),
    surveyScheduled:
      (scheduledRule?.actions || [])
        .slice(1) // Ignore first rule as it's represented in another field (tag scheduled)
        .map(decorateWithClientId) ?? [],
  };

  const surveyDelay = findRuleCondition(sentRule, RuleConditionTypesEnum.DELAY_SINCE_TRIGGER)?.params;

  const csatBundleError = !csatReplyAction ? CsatRuleErrorsEnum.MISSING_AUTO_REPLY : undefined;

  const timeDelay: CsatDelay = surveyDelay ?? CsatDelay.empty; // Fallback to an empty value

  return {
    ruleName: recipeName || '',
    conditions: mapToClientConditions(conditions),
    tags,
    actions,
    timeDelay,
    csatResponse: csatReplyAction?.params.response,
    initialRules: {
      scheduledRule,
      sentRule,
      unscheduleRule,
    },
    csatBundleError,
  };
}

// Utils.

/**
 * Find the first rule in csatRules that matches the ruleRole parameter
 */
function maybeFindCsatRule(
  csatRules: ReadonlyArray<RuleModelStrict>,
  ruleRole: CsatRuleRolesEnum,
): RuleModelStrict | undefined {
  return csatRules.find((rule) => rule.role?.startsWith(ruleRole));
}

function getConditions(sentRule: RuleModelStrict): ReadonlyArray<ReadonlyArray<RuleCondition>> {
  const conditions = splitANDconditions(sentRule);

  // The first AND conditions are CSAT internals and not to be modified.
  // They are hidden from the user.
  const [deletedConditions, keptConditions] = splitAt(conditions, HIDDEN_SENT_FIRST_CONDITIONS_COUNT);

  // Sanity check the hidden conditions.
  const unexpectedConditions = deletedConditions
    .reduce((a, b) => a.concat(b), [])
    .filter((cond) => !HIDDEN_SENT_FIRST_CONDITIONS.includes(cond.type));

  if (unexpectedConditions.length > 0) {
    const names = unexpectedConditions.map((cond) => cond.type).join(', ');
    console.error(
      `Found ${unexpectedConditions.length} unexpected implicit conditions ${names}`,
      sentRule.compoundConditions,
    );
  }

  return keptConditions;
}
